import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Sunrise, Moon, Sparkles, Coffee, Target, BookOpen, Wind, Waves } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const Index = () => {
  const { user } = useCurrentUser();
  const [greeting, setGreeting] = useState('');
  const [showSplash, setShowSplash] = useState(true);
  const navigate = useNavigate();

  useSeoMeta({
    title: 'Zenstr - Your Mindful Sanctuary',
    description: 'Daily rituals, meditation, and peaceful connection on Nostr.',
  });

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) setGreeting('Good Morning');
    else if (hour >= 12 && hour < 17) setGreeting('Good Afternoon');
    else if (hour >= 17 && hour < 21) setGreeting('Good Evening');
    else setGreeting('Good Night');

    const shown = sessionStorage.getItem('zenstr-splash');
    if (shown) {
      setShowSplash(false);
    } else {
      setTimeout(() => {
        setShowSplash(false);
        sessionStorage.setItem('zenstr-splash', 'true');
      }, 2500);
    }
  }, []);

  const userName = user?.profile?.metadata?.name || user?.profile?.metadata?.display_name || 'friend';
  const userPicture = user?.profile?.metadata?.picture;

  const tiles = [
    { title: 'Morning Note', icon: Sunrise, path: '/morning', color: 'from-orange-500 to-pink-500' },
    { title: 'Night Note', icon: Moon, path: '/night', color: 'from-indigo-500 to-purple-500' },
    { title: 'Daily Quote', icon: Sparkles, path: '/quote', color: 'from-yellow-500 to-amber-500' },
    { title: 'Buy Coffee', icon: Coffee, path: '/coffee', color: 'from-amber-500 to-orange-500' },
    { title: 'Life Goals', icon: Target, path: '/goals', color: 'from-green-500 to-emerald-500' },
    { title: 'Daily Journal', icon: BookOpen, path: '/journal', color: 'from-blue-500 to-cyan-500' },
    { title: 'Breathe', icon: Wind, path: '/breathe', color: 'from-teal-500 to-cyan-500' },
    { title: 'Sleep Sounds', icon: Waves, path: '/sounds', color: 'from-cyan-500 to-blue-500' },
  ];

  const startRitual = () => {
    const hour = new Date().getHours();
    const isMorning = hour >= 5 && hour < 17;
    navigate(isMorning ? '/morning-ritual' : '/evening-ritual');
  };

  if (showSplash) {
    return (
      <div className="min-h-screen bg-zen flex items-center justify-center">
        <div className="text-center enter">
          <h1 className="text-8xl md:text-9xl font-display font-bold text-white mb-6 drop-shadow-2xl">
            {greeting}
          </h1>
          <div className="flex justify-center gap-2">
            {[0, 1, 2].map(i => (
              <div 
                key={i} 
                className="w-3 h-3 rounded-full bg-white/60"
                style={{ animation: 'breathe-cycle 2s ease-in-out infinite', animationDelay: `${i * 0.3}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🧘‍♀️</span>
            <div>
              <h1 className="text-lg font-display font-bold">Zenstr</h1>
              <p className="text-xs text-muted-foreground">Peaceful sanctuary</p>
            </div>
          </div>

          {user ? (
            <Link to="/profile">
              <Avatar className="w-10 h-10 hover:scale-105 transition-transform cursor-pointer ring-2 ring-primary/30">
                <AvatarImage src={userPicture} />
                <AvatarFallback className="bg-primary/20">
                  {userName.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
          ) : (
            <LoginArea className="max-w-48" />
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-4xl mx-auto space-y-12 enter">
          <div className="space-y-6">
            <h2 className="text-7xl md:text-8xl lg:text-9xl font-display font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent leading-tight">
              {greeting}
              {user && ','}
            </h2>
            {user && (
              <p className="text-5xl md:text-6xl font-display font-light text-foreground/90">
                {userName}
              </p>
            )}
          </div>

          {user && (
            <Button
              onClick={startRitual}
              size="lg"
              className="bg-gradient-to-r from-primary to-accent hover:opacity-90 px-12 py-8 text-xl rounded-full shadow-2xl hover:shadow-primary/50 transition-all hover:scale-105"
            >
              {greeting === 'Good Morning' || greeting === 'Good Afternoon' 
                ? 'Start Morning Ritual' 
                : 'Start Evening Ritual'}
            </Button>
          )}
        </div>
      </section>

      {/* Tiles */}
      <section className="container mx-auto px-6 py-12 max-w-6xl">
        <h3 className="text-center text-sm uppercase tracking-widest text-muted-foreground mb-8 font-semibold">
          Explore Features
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {tiles.map((tile, i) => (
            <Link key={tile.path} to={tile.path}>
              <Card 
                className="h-full group hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 hover:-translate-y-2 bg-card/50 backdrop-blur border-border/50 enter"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="p-8 flex flex-col items-center space-y-4">
                  <div className={`p-5 rounded-2xl bg-gradient-to-br ${tile.color} group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <tile.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-display font-semibold text-lg">{tile.title}</h3>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-12 text-center">
        <p className="text-sm text-muted-foreground">
          Vibed with <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Shakespeare</a>
        </p>
      </footer>
    </div>
  );
};

export default Index;
